import { useNavigate, Link } from 'react-router-dom';
import { useState, useEffect, useRef, TouchEvent } from 'react';
import styles from './ActiveScenarioSessionTopBar.module.css';
import { shareIcon, clipboardIcon } from '../../assets/icons';

interface ActiveScenarioSessionTopBarProps {
  displayName: string;
  scenarioIcon?: string;
  scenarioSessionId?: string;
  scenarioDefinitionId?: string;
  onBookClick?: () => void;
  onDrawerToggle?: () => void;
  isMobile?: boolean;
  onInfoClick?: () => void;
  onModeChange?: (isAutoMode: boolean) => void;
  initialMode?: 'auto' | 'manual';
  renderNotificationInTopBar?: boolean;
}

export const ModeNotification = ({ 
  isAutoMode, 
  show,
  className = '',
  position = 'default'
}: { 
  isAutoMode: boolean; 
  show: boolean;
  className?: string;
  position?: 'default' | 'above-character-bar';
}) => {
  if (!show) return null;
  
  // Touch event handlers to prevent vertical scrolling on mobile
  const handleTouchStart = (e: TouchEvent<HTMLDivElement>): void => {
    // Stop propagation to prevent parent handlers from firing
    e.stopPropagation();
  };

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>): void => {
    // Completely prevent scrolling on mobile
    e.stopPropagation();
    
    // Detect vertical scrolling and prevent default behavior
    const touchY = e.touches[0].clientY;
    const isVerticalScroll = Math.abs(touchY - e.touches[0].pageY) > 
                            Math.abs(e.touches[0].clientX - e.touches[0].pageX);
    
    if (isVerticalScroll) {
      e.preventDefault();
    }
  };

  const handleTouchEnd = (e: TouchEvent<HTMLDivElement>): void => {
    // Stop propagation to prevent parent handlers from firing
    e.stopPropagation();
  };
  
  return (
    <div 
      className={`${styles.notification} ${styles.modeNotification} ${className} ${styles.animatedNotification}`}
      data-position={position}
      key={isAutoMode ? 'auto' : 'manual'}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <span>
        {isAutoMode 
          ? "Characters will now respond automatically" 
          : "Select who answers your messages"}
      </span>
    </div>
  );
};

const ActiveScenarioSessionTopBar = ({ 
  displayName, 
  scenarioIcon,
  scenarioSessionId,
  scenarioDefinitionId,
  onBookClick,
  onDrawerToggle,
  isMobile,
  onInfoClick,
  onModeChange,
  initialMode = 'auto',
  renderNotificationInTopBar = true
}: ActiveScenarioSessionTopBarProps) => {
  const navigate = useNavigate();
  const [showCopiedNotification, setShowCopiedNotification] = useState(false);
  const [isAutoMode, setIsAutoMode] = useState(initialMode === 'auto');
  const [showModeNotification, setShowModeNotification] = useState(false);
  const notificationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleBackClick = () => {
    navigate('/chats', { state: { activeTab: 'scenarios' } });
  };

  const handleShare = () => {
    // Copy the scenario URL to clipboard with the correct format
    const shareUrl = `${window.location.origin}/scenarios/${scenarioDefinitionId}`;
    navigator.clipboard.writeText(shareUrl)
      .then(() => {
        setShowCopiedNotification(true);
        setTimeout(() => {
          setShowCopiedNotification(false);
        }, 3000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  const handleModeToggle = () => {
    const newMode = !isAutoMode;
    setIsAutoMode(newMode);
    
    // Clear any existing timeout to prevent conflicting timeouts
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }
    
    // Show mode change notification
    setShowModeNotification(true);
    
    // Store the new timeout reference
    notificationTimeoutRef.current = setTimeout(() => {
      setShowModeNotification(false);
      notificationTimeoutRef.current = null;
    }, 3000);
    
    if (onModeChange) {
      onModeChange(newMode);
    }
  };

  // Touch event handlers to prevent vertical scrolling on mobile
  const handleTouchStart = (e: TouchEvent<HTMLDivElement>): void => {
    // Always stop propagation to prevent parent handlers from firing
    e.stopPropagation();
  };

  const handleTouchMove = (e: TouchEvent<HTMLDivElement>): void => {
    // Always stop propagation to prevent parent handlers from firing
    e.stopPropagation();
    
    // Detect vertical scrolling
    const touchY = e.touches[0].clientY;
    const isVerticalScroll = Math.abs(touchY - e.touches[0].pageY) > 
                            Math.abs(e.touches[0].clientX - e.touches[0].pageX);
    
    // If it's a vertical scroll, prevent the default behavior
    if (isVerticalScroll) {
      e.preventDefault();
    }
  };

  const handleTouchEnd = (e: TouchEvent<HTMLDivElement>): void => {
    // Stop propagation to prevent parent handlers from firing
    e.stopPropagation();
  };

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <div 
        className={styles.topBar}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <button 
          className={styles.backButton} 
          onClick={handleBackClick}
          aria-label="Back to chats"
        >
          ‹
        </button>
        
        <div className={styles.scenarioInfo}>
          {scenarioIcon && (
            <Link to={`/scenarios/${scenarioDefinitionId}`}>
              <img 
                src={scenarioIcon} 
                alt={displayName} 
                className={styles.scenarioIcon} 
              />
            </Link>
          )}
          <h1 title={displayName}>{displayName}</h1>
        </div>

        <div className={styles.topBarControls}>
          <button 
            className={`${styles.modeToggle} ${isAutoMode ? styles.modeToggleActive : ''}`}
            onClick={handleModeToggle}
            aria-label={isAutoMode ? "Switch to Manual Mode" : "Switch to Auto Mode"}
            title={isAutoMode ? "Currently in Auto Mode" : "Currently in Manual Mode"}
          >
            <span className={styles.modeToggleTrack}>
              <span className={styles.modeToggleKnob}></span>
            </span>
            <span className={styles.modeToggleLabel}>
              {isAutoMode ? "Auto" : "Manual"}
            </span>
          </button>
          
          <button 
            className={styles.shareButton} 
            onClick={handleShare}
            aria-label="Share scenario"
          >
            <img src={shareIcon} alt="Share" className={styles.shareIcon} />
          </button>
        </div>
      </div>

      {showCopiedNotification && (
        <div 
          className={styles.notification}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <img src={clipboardIcon} alt="Copied" className={styles.notificationIcon} />
          <span>Copied to clipboard</span>
        </div>
      )}

      {renderNotificationInTopBar && (
        <ModeNotification isAutoMode={isAutoMode} show={showModeNotification} />
      )}
    </>
  );
};

export default ActiveScenarioSessionTopBar; 